import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _, { cloneDeep } from 'lodash';
import { Alert } from 'reactstrap';
import { SSE_BUYER, SSEClient } from '../api/SSEClient';
import { clearMyBids, searchMyBids, setBidsFilter } from './MyBidsActions';
import SearchBidModal from '../search/bidModals/SearchBidModal';
import { placeBid } from '../search/SearchActions';
import useDefaultBidLocationId from '../common/hooks/useDefaultBidLocationId';
import RouterPaths from '../constants/RouterPaths';
import { getAuction, handleAuctionEvent } from '../myvehicles/MyVehiclesActions';
import { MY_BIDS_REFRESH_SUCCESS } from './MyBidsReducer';
import { BidStatusType } from '../constants/BidStatusType';
import AuctionListTemplate, {
  AuctionListTemplateContextType,
} from '../common/components/new/AuctionListTemplate';
import AuctionListItem from '../common/components/new/AuctionListItem';
import { auctionListType } from '../constants/AuctionListType';

const sseClient = new SSEClient(SSE_BUYER);

function MyBidsPage({ history, location }) {
  const dispatch = useDispatch();
  const organizationLocationId = useDefaultBidLocationId();
  const itemsRef = useRef({});
  const [scrollTo, setScrollTo] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [offset, setOffset] = useState(0);
  const [auctionsIds, setAuctionsIds] = useState([]);
  const [search, setSearch] = useState('');
  const [currentBidAuction, setCurrentBidAuction] = useState(null);
  const [showBidModal, setShowBidModal] = useState(false);
  const [bidsNotifications, setBidsNotifications] = useState({});
  const latestCriteriaRef = useRef({});
  const [currentSort, setCurrentSort] = useState(null);

  const hasMore = useSelector((state) => state.myBids.get('hasMore'));
  const bidsSearchFilter = useSelector((state) => state.myBids.get('filter'));
  const searchQuery = useSelector((state) => state.search.get('searchQuery'));
  const searchItems = useSelector((state) => state.myBids.get('items').toJS());
  const searchTotalItems = useSelector((state) => state.myBids.get('totalItems'));
  const isBuyer = useSelector((state) => state.auth.get('isBuyer'));
  const isSeller = useSelector((state) => state.auth.get('isSeller'));
  const [hasAccess, setHasAccess] = useState(false);

  const updateSearchItems = (newItem) => {
    dispatch({
      type: MY_BIDS_REFRESH_SUCCESS,
      payload: {
        item: newItem,
      },
    });
  };

  useEffect(() => {
    if (!location || !location.state || !location.state.offset) {
      clearResults();
    }
  }, []);

  useEffect(() => {
    if (location) {
      const { hash } = location;
      if (hash && hash === '#bidsFilter') {
        history.replace(RouterPaths.MY_BIDS);
      } else {
        dispatch(setBidsFilter((location.state && location.state.filter) || bidsSearchFilter));
      }
    }

    return () => {
      dispatch(setBidsFilter(BidStatusType.PLACED_BIDS)); // reset to first filter
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isBuyer) {
      setHasAccess(false);
      if (isSeller) {
        return history.push(RouterPaths.MY_VEHICLES);
      }

      return history.push(RouterPaths.SEARCH);
    }
    return setHasAccess(true);
  }, [isBuyer, isSeller, history]);

  useEffect(() => {
    sseClient.listen(auctionsIds, (response) => {
      dispatch(
        handleAuctionEvent(response, () => {
          if (response?.auctionId) {
            dispatch(
              getAuction(response.auctionId, (auction) => {
                updateSearchItems(auction);
              }),
            );
          }
        }),
      );
    });
    return () => {
      sseClient.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionsIds, dispatch]);

  useEffect(() => {
    if (location && location.state && location.state.offset && location.state.auctionId) {
      setOffset(_.get(location, 'state.offset', 0) + 1);
      setScrollTo(_.get(location, 'state.auctionId'));
      history.replace({ ...history.location, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, bidsSearchFilter, search, dispatch]);

  const clearResults = () => {
    dispatch(clearMyBids());
    setOffset(0);
    setAuctionsIds([]);
    setLoaded(false);
  };

  const toggleBidModal = () => {
    setShowBidModal(false);
  };

  const addNotification = (id, message, className) => {
    const notifications = cloneDeep(bidsNotifications);
    notifications[id] = {
      className,
      message,
    };
    setBidsNotifications(notifications);
  };

  const handlePlaceBid = (amount) => {
    dispatch(
      placeBid(
        currentBidAuction?.id,
        amount * 100,
        (response) => {
          const { success, auctionId, result } = response;
          if (!success) {
            addNotification(auctionId, result, 'warning');
          } else {
            let message;

            const bidAmount = `£${amount.toLocaleString('en-GB')}`;

            if (result.isHighestBid) {
              if (result.proxyBidInPence) {
                message = `Your bid of ${result.proxyBidInPence} is the highest. Your maximum bid is ${bidAmount}`;
              } else {
                message = `Your bid of ${bidAmount} is the highest`;
              }
            } else {
              message = `Your bid of ${bidAmount} is not the highest - try again`;
            }
            addNotification(auctionId, message, 'warning');
          }
        },
        organizationLocationId,
      ),
    );
  };

  return (
    <AuctionListTemplate
      context={AuctionListTemplateContextType.BUYING}
      searchItems={searchItems}
      onCriteriaChange={(crit) => {
        latestCriteriaRef.current = crit || {};
      }}
      onSortChange={(newSort) => {
        setCurrentSort(newSort);
      }}
    >
      {searchTotalItems > 0 ? (
        <>
          {searchItems.map((item) => (
            <AuctionListItem
              item={item}
              key={item.id}
              context={AuctionListTemplateContextType.BUYING}
              listType={auctionListType.MY_BIDS}
            />
          ))}
        </>
      ) : (
        <div className="mt-2">
          {searchQuery ? (
            <Alert color="info">Oops, we could not find any result for the given phrase</Alert>
          ) : (
            <Alert color="info">Looks like there are no offers yet.</Alert>
          )}
        </div>
      )}
      {showBidModal && (
        <SearchBidModal
          toggleShow={toggleBidModal}
          item={currentBidAuction}
          handlePlaceBid={handlePlaceBid}
        />
      )}
    </AuctionListTemplate>
  );
}

export default MyBidsPage;
